if (!customElements.get('ap-campaign-hero')) {
  class AromaticaCampaignHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.playButton = this.querySelector('[data-ap-hero-play]');
      this.soundButton = this.querySelector('[data-ap-hero-sound]');
      this.videoSources = [...(this.video?.querySelectorAll('source[data-src]') || [])];

      if (!this.video) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection?.saveData === true;
      this.userPaused = reducedMotion || saveData;
      this.videoLoaded = false;
      this.isIntersecting = true;
      this.video.muted = true;
      if (this.userPaused) {
        this.video.pause();
      } else {
        this.startOnInteraction = (event) => {
          if (event.target.closest?.('[data-ap-hero-play], [data-ap-hero-sound]')) return;
          this.startPlayback();
        };
        ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
          window.addEventListener(eventName, this.startOnInteraction, { once: true });
        });
        this.autoplayTimer = window.setTimeout(() => this.startPlayback(), 8000);
      }

      this.playButton?.addEventListener('click', () => this.togglePlayback());
      this.soundButton?.addEventListener('click', () => this.toggleSound());
      this.video.addEventListener('play', () => this.updatePlayState());
      this.video.addEventListener('pause', () => this.updatePlayState());
      this.video.addEventListener('volumechange', () => this.updateSoundState());

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isIntersecting = entry.isIntersecting;
        if (entry.isIntersecting && !this.userPaused && this.videoLoaded) {
          this.video.play().catch(() => this.updatePlayState());
        } else if (!entry.isIntersecting) {
          this.video.pause();
        }
      }, { threshold: 0.08 });

      this.visibilityObserver.observe(this);
      this.updatePlayState();
      this.updateSoundState();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      window.clearTimeout(this.autoplayTimer);
      ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
        window.removeEventListener(eventName, this.startOnInteraction);
      });
    }

    startPlayback() {
      if (this.userPaused || !this.isIntersecting) return;

      if (!this.videoLoaded) {
        this.videoSources.forEach((source) => {
          source.src = source.dataset.src;
        });
        this.video.load();
        this.videoLoaded = true;
      }

      this.video.play().catch(() => this.updatePlayState());
    }

    togglePlayback() {
      if (this.video.paused) {
        this.userPaused = false;
        this.startPlayback();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.updateSoundState();
    }

    updatePlayState() {
      if (!this.playButton) return;
      const paused = this.video.paused;
      this.playButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.playButton.setAttribute('aria-label', paused ? this.playButton.dataset.playLabel : this.playButton.dataset.pauseLabel);
      const controlLabel = this.playButton.querySelector('[data-ap-hero-control-label]');
      if (controlLabel) {
        controlLabel.textContent = paused
          ? this.playButton.dataset.playLabel
          : this.playButton.dataset.pauseLabel;
      }
    }

    updateSoundState() {
      if (!this.soundButton) return;
      const muted = this.video.muted;
      this.soundButton.setAttribute('aria-pressed', muted ? 'false' : 'true');
      this.soundButton.setAttribute('aria-label', muted ? this.soundButton.dataset.soundOnLabel : this.soundButton.dataset.soundOffLabel);
    }
  }

  customElements.define('ap-campaign-hero', AromaticaCampaignHero);
}
